/**
 * TASK MAPPERS — conversione tra tipi UI (union string) e DTO REST (int32).
 *
 * Pattern di riferimento per tutti gli enum mappati string<->int tra
 * frontend e backend CRM.
 *
 * Convenzione del progetto:
 * - I tipi UI usano union string per leggibilita'
 *   (TaskType = 'activity' | 'call' | ...).
 * - Il backend REST espone int32, allineato a `getValue()` degli enum
 *   Java in `it.dvel.crm.util.constants.Task*` (modules/crm-util).
 * - La conversione string<->int avviene ESCLUSIVAMENTE ai boundary
 *   (dentro *Api.ts, in toDto/fromDto invocati prima del POST/PATCH e
 *   dopo il GET). Redux store e componenti UI vedono solo stringhe.
 * - `status` non viaggia in TaskInput (POST/PATCH generico): la
 *   transizione di stato passa per l'endpoint dedicato
 *   PATCH /task/{taskId}/status con body `TaskStatusInput` (helper
 *   `taskStatusToInput` / `taskStatusInputFromDto` sotto).
 *
 * SYNC CROSS-LAYER — se il backend aggiunge/toglie/rinomina valori, le
 * tabelle `*_TO_INT` qui sotto vanno aggiornate insieme a:
 *   - `taskTypes.ts` (type union + array UI TASK_TYPES/TASK_PRIORITIES) e
 *     `taskRecurrentUnits.ts` (union + TASK_RECURRENT_UNITS) con chiavi i18n
 *   - `locales/{it,en}.json` (chiavi task.types.* / task.priorities.* /
 *     task.statuses.* / task.recurrent.units.*)
 *   - Java: `modules/crm-util/.../{TaskType,TaskPriority,TaskStatus,TaskRecurrentUnit}.java`
 * Le tabelle sono l'unica fonte di verita' lato client per il mapping
 * int↔string; se divergono dal backend, `enumFromDto` (fallback soft)
 * loggherà `int sconosciuto N` in console.
 */

import { enumFromDto, pickAuditFields } from '@crm/shared';
import type { Task, TaskType, TaskPriority, TaskStatus } from './taskTypes';
import type { TaskRecurrentUnit } from './taskRecurrentUnits';

// ─── TaskType ──────────────────────────────────────────────────────────
// SYNC with TaskType.java: ACTIVITY=1, CALL=2, EMAIL=3, MEETING=4,
// NOTE=5, WHATSAPP_CALL=6, LINKEDIN_CALL=7
// File: modules/crm-util/src/main/java/it/dvel/crm/util/constants/TaskType.java
const TASK_TYPE_TO_INT: Record<TaskType, number> = {
  activity: 1,
  call: 2,
  email: 3,
  meeting: 4,
  note: 5,
  whatsapp_call: 6,
  linkedin_call: 7,
};
const TASK_TYPE_FROM_INT: Record<number, TaskType> = invert(TASK_TYPE_TO_INT);

// ─── TaskPriority ──────────────────────────────────────────────────────
// SYNC with TaskPriority.java: NONE=1, LOW=2, MEDIUM=3, HIGH=4
// File: modules/crm-util/src/main/java/it/dvel/crm/util/constants/TaskPriority.java
const TASK_PRIORITY_TO_INT: Record<TaskPriority, number> = {
  none: 1,
  low: 2,
  medium: 3,
  high: 4,
};
const TASK_PRIORITY_FROM_INT: Record<number, TaskPriority> = invert(TASK_PRIORITY_TO_INT);

// ─── TaskStatus ────────────────────────────────────────────────────────
// SYNC with TaskStatus.java: TODO=1, IMMINENT=2, EXPIRING_TODAY=3, EXPIRED=4, COMPLETED=5
// File: modules/crm-util/src/main/java/it/dvel/crm/util/constants/TaskStatus.java
const TASK_STATUS_TO_INT: Record<TaskStatus, number> = {
  todo: 1,
  imminent: 2,
  expiring_today: 3,
  expired: 4,
  completed: 5,
};
const TASK_STATUS_FROM_INT: Record<number, TaskStatus> = invert(TASK_STATUS_TO_INT);

// ─── TaskRecurrentUnit ─────────────────────────────────────────────────
// SYNC with TaskRecurrentUnit.java: NONE=0, DAY=1, WORKING_DAY=2, WEEK=3,
// MONTH=4, YEAR=5
// File: modules/crm-util/src/main/java/it/dvel/crm/util/constants/TaskRecurrentUnit.java
const TASK_RECURRENT_UNIT_TO_INT: Record<TaskRecurrentUnit, number> = {
  none: 0,
  day: 1,
  'working-day': 2,
  week: 3,
  month: 4,
  year: 5,
};
const TASK_RECURRENT_UNIT_FROM_INT: Record<number, TaskRecurrentUnit> = invert(
  TASK_RECURRENT_UNIT_TO_INT,
);

// ─── API di mapping singolo enum ───────────────────────────────────────
//
// Il fallback soft + warn e' in `enumFromDto` (@crm/shared): gestisce i
// record con enum fuori-range (source=0, valori legacy) senza crashare
// la list. Vedi docblock in enumFromDto.ts.

// Esportati: oltre ai mapper di questo modulo li usa la configurazione dell'azione
// di automazione "crea attività" (CreateTaskActionConfig), che persiste tipo e
// priorità come int perché è la forma che il provider Java rilegge. Riusare
// queste due coppie invece di scriverne altre tiene UNA sola tabella di
// conversione, che è anche quella elencata dal commento SYNC di taskTypes.ts.
export const taskTypeToDto = (v: TaskType): number => TASK_TYPE_TO_INT[v];
export const taskTypeFromDto = (v: number): TaskType =>
  enumFromDto('TaskType', v, TASK_TYPE_FROM_INT, 'activity');

export const taskPriorityToDto = (v: TaskPriority): number => TASK_PRIORITY_TO_INT[v];
export const taskPriorityFromDto = (v: number): TaskPriority =>
  enumFromDto('TaskPriority', v, TASK_PRIORITY_FROM_INT, 'none');

// Esportato: usato dai mock MSW per emettere lo status come int32 (shape backend).
export const taskStatusToDto = (v: TaskStatus): number => TASK_STATUS_TO_INT[v];
// Esportato: usato anche da useEntityActivities per convertire ActivitySummary.status
// (int32 dal bulk) nel simbolico TaskStatus della UI.
export const taskStatusFromDto = (v: number): TaskStatus =>
  enumFromDto('TaskStatus', v, TASK_STATUS_FROM_INT, 'todo');

const taskRecurrentUnitToDto = (v: TaskRecurrentUnit): number => TASK_RECURRENT_UNIT_TO_INT[v];
const taskRecurrentUnitFromDto = (v: number): TaskRecurrentUnit =>
  enumFromDto('TaskRecurrentUnit', v, TASK_RECURRENT_UNIT_FROM_INT, 'none');

// ─── DTO completo del Task (shape REST, con int al posto degli enum) ───

/**
 * Shape del Task come esce dal REST (/o/crm/v1_0/task/{taskId}).
 * Rispecchia lo schema Task nel rest-openapi.yaml. Gli enum sono int32.
 * Usato solo dentro taskApi/taskMappers: lo store/UI vede solo `Task`.
 */
export interface TaskDto {
  taskId: number;
  companyId: number;
  groupId: number;
  userId: number;
  userName: string;
  createDate: string;
  modifiedDate: string;
  title: string;
  type: number;
  priority: number;
  status: number;
  /** Stato del funnel (colonna della bacheca). 0 = non collocato. */
  currentStateId?: number;
  expirationDate: string | null;
  remindDate: string | null;
  recurrent: boolean;
  recurrentValue: number;
  recurrentUnit: number;
  assignedUserId: number;
  // Backend usa 0 come "non valorizzato"; normalizziamo a null per lo store.
  queueId: number;
  notes: string | null;
}

/** DTO -> modello UI: chiamato sulle response list/getOne. */
export function taskFromDto(dto: TaskDto): Task {
  return {
    taskId: dto.taskId,
    ...pickAuditFields(dto),
    title: dto.title,
    type: taskTypeFromDto(dto.type),
    priority: taskPriorityFromDto(dto.priority),
    status: taskStatusFromDto(dto.status),
    // Assente sul filo = non collocato. Il server lo manda sempre; un mock potrebbe non farlo.
    currentStateId: dto.currentStateId ?? 0,
    expirationDate: dto.expirationDate,
    remindDate: dto.remindDate,
    recurrent: dto.recurrent,
    recurrentValue: dto.recurrentValue,
    recurrentUnit: taskRecurrentUnitFromDto(dto.recurrentUnit),
    assignedUserId: dto.assignedUserId,
    queueId: dto.queueId === 0 ? null : dto.queueId,
    notes: dto.notes,
  };
}

/**
 * Modello UI -> DTO REST. Usato dai mock MSW per simulare la shape del
 * backend (enum come int32, queueId 0=non valorizzato). Non invocato
 * dall'app reale.
 */
export function taskToDto(t: Task): TaskDto {
  return {
    taskId: t.taskId,
    ...pickAuditFields(t),
    title: t.title,
    type: taskTypeToDto(t.type),
    priority: taskPriorityToDto(t.priority),
    status: taskStatusToDto(t.status),
    currentStateId: t.currentStateId,
    expirationDate: t.expirationDate,
    remindDate: t.remindDate,
    recurrent: t.recurrent,
    recurrentValue: t.recurrentValue,
    recurrentUnit: taskRecurrentUnitToDto(t.recurrentUnit),
    assignedUserId: t.assignedUserId,
    queueId: t.queueId ?? 0,
    notes: t.notes,
  };
}

/**
 * Shape del payload accettato dal backend in POST/PATCH. Enum come
 * int32; campi audit e status (gestito server-side via endpoint dedicato
 * /task/{id}/status) non sono accettati qui. `queueId` 0 significa "non
 * valorizzato".
 */
export interface TaskInput {
  title?: string;
  type?: number;
  priority?: number;
  expirationDate?: string | null;
  remindDate?: string | null;
  recurrent?: boolean;
  recurrentValue?: number;
  recurrentUnit?: number;
  assignedUserId?: number;
  queueId?: number;
  notes?: string | null;
  currentStateId?: number;
}

/** Payload create UI -> DTO input. I campi di ricorrenza sono significativi
 * solo se recurrent è true; a recurrent=false si invia recurrentUnit='none'
 * e recurrentValue=0 (sentinella backend). */
export function taskCreateToInput(p: {
  title: string;
  type: TaskType;
  priority: TaskPriority;
  expirationDate: string | null;
  remindDate: string | null;
  recurrent: boolean;
  recurrentValue: number;
  recurrentUnit: TaskRecurrentUnit;
  assignedUserId: number;
  queueId: number | null;
  notes: string | null;
  currentStateId?: number;
}): TaskInput {
  return {
    title: p.title,
    type: taskTypeToDto(p.type),
    priority: taskPriorityToDto(p.priority),
    expirationDate: p.expirationDate,
    remindDate: p.remindDate,
    recurrent: p.recurrent,
    recurrentValue: p.recurrentValue,
    recurrentUnit: taskRecurrentUnitToDto(p.recurrentUnit),
    assignedUserId: p.assignedUserId,
    queueId: p.queueId ?? 0,
    notes: p.notes,
    // 0 = nessuna collocazione, ed e' il caso normale: il server non scrive nessuna riga.
    currentStateId: p.currentStateId ?? 0,
  };
}

/** Payload update UI -> DTO input parziale (PATCH). */
export function taskUpdateToInput(p: {
  title?: string;
  type?: TaskType;
  priority?: TaskPriority;
  expirationDate?: string | null;
  remindDate?: string | null;
  recurrent?: boolean;
  recurrentValue?: number;
  recurrentUnit?: TaskRecurrentUnit;
  assignedUserId?: number;
  queueId?: number | null;
  notes?: string | null;
  currentStateId?: number;
}): TaskInput {
  const out: TaskInput = {};
  if (p.title !== undefined) out.title = p.title;
  if (p.type !== undefined) out.type = taskTypeToDto(p.type);
  if (p.priority !== undefined) out.priority = taskPriorityToDto(p.priority);
  if (p.expirationDate !== undefined) out.expirationDate = p.expirationDate;
  if (p.remindDate !== undefined) out.remindDate = p.remindDate;
  if (p.recurrent !== undefined) out.recurrent = p.recurrent;
  if (p.recurrentValue !== undefined) out.recurrentValue = p.recurrentValue;
  if (p.recurrentUnit !== undefined) out.recurrentUnit = taskRecurrentUnitToDto(p.recurrentUnit);
  if (p.assignedUserId !== undefined) out.assignedUserId = p.assignedUserId;
  if (p.queueId !== undefined) out.queueId = p.queueId ?? 0;
  if (p.notes !== undefined) out.notes = p.notes;
  // Lo stato del funnel viaggia nello stesso payload ma lato server ha un canale dedicato, come
  // status: il resource lo intercetta e chiama updateCurrentState.
  if (p.currentStateId !== undefined) out.currentStateId = p.currentStateId;
  return out;
}

/**
 * Body del PATCH /task/{taskId}/status. Single field int32 allineato a
 * TaskStatus.java; serializzato come `{status: number}`.
 */
export interface TaskStatusInput {
  status: number;
}

/** Status UI -> body PATCH /task/{taskId}/status. */
export function taskStatusToInput(status: TaskStatus): TaskStatusInput {
  return { status: taskStatusToDto(status) };
}

/** Body PATCH status -> patch UI. Usato dai mock MSW. */
export function taskStatusInputFromDto(input: TaskStatusInput): { status: TaskStatus } {
  return { status: taskStatusFromDto(input.status) };
}

/**
 * DTO input (PATCH body) -> Partial<Task> UI. Usato dai mock MSW per
 * applicare il patch al mockTasks storage (shape UI).
 */
export function taskInputFromDto(input: TaskInput): Partial<Task> {
  const out: Partial<Task> = {};
  if (input.title !== undefined) out.title = input.title;
  if (input.type !== undefined) out.type = taskTypeFromDto(input.type);
  if (input.priority !== undefined) out.priority = taskPriorityFromDto(input.priority);
  if (input.expirationDate !== undefined) out.expirationDate = input.expirationDate;
  if (input.remindDate !== undefined) out.remindDate = input.remindDate;
  if (input.recurrent !== undefined) out.recurrent = input.recurrent;
  if (input.recurrentValue !== undefined) out.recurrentValue = input.recurrentValue;
  if (input.recurrentUnit !== undefined) out.recurrentUnit = taskRecurrentUnitFromDto(input.recurrentUnit);
  if (input.assignedUserId !== undefined) out.assignedUserId = input.assignedUserId;
  if (input.queueId !== undefined) out.queueId = input.queueId === 0 ? null : input.queueId;
  if (input.notes !== undefined) out.notes = input.notes;
  // Senza questa riga il mock accetterebbe la PATCH e butterebbe via lo stato: la card tornerebbe
  // indietro dopo il trascinamento, senza errori e senza spiegazioni.
  if (input.currentStateId !== undefined) out.currentStateId = input.currentStateId;
  return out;
}

// Helper: inverte un Record<K, number> in un Record<number, K>.
function invert<K extends string>(src: Record<K, number>): Record<number, K> {
  const out = {} as Record<number, K>;
  for (const key of Object.keys(src) as K[]) out[src[key]] = key;
  return out;
}
